import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref as dbRef, get, push } from "firebase/database";
import { uploadImage } from "../utils/uploadImage";
import { useAuth } from "../context/AuthContext";
import { FileText, Plus, X, Camera, AlertCircle, MessageCircle, ArrowLeft } from "lucide-react";

const SUBJECTS = [
  "Mathematics", "Physics", "Chemistry", "Engineering Drawing",
  "Programming / DSA", "DBMS", "Operating Systems", "Computer Networks",
  "Data Science / ML", "Electronics", "Mechanics", "Circuit Theory",
  "Management", "Economics", "English", "Other"
];

const NOTE_TYPES = ["Handwritten", "Typed / Printed", "Digital (PDF)", "Solved Papers", "Question Bank"];

function NoteCard({ note }) {
  const waMsg = encodeURIComponent(
    `Hi! I'm interested in your notes "${note.title}" (${note.subject}, Sem ${note.semester}) on CampusSwap. Is it still available?`
  );
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
      <div className="flex items-start gap-3">
        <div className="w-16 h-16 rounded-xl bg-yellow-50 flex items-center justify-center flex-shrink-0 overflow-hidden border border-yellow-100">
          {note.imageUrl
            ? <img src={note.imageUrl} alt={note.title} className="w-full h-full object-cover rounded-xl" />
            : <FileText size={28} className="text-yellow-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-800 text-sm line-clamp-1">{note.title}</p>
          <p className="text-xs text-gray-500 mt-0.5">{note.subject} · Sem {note.semester}</p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full">{note.type}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${note.price === 0 ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
              {note.price === 0 ? "FREE" : `₹${note.price}`}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-1">{note.sellerName} · {note.college}</p>
        </div>
      </div>
      {note.description && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{note.description}</p>}
      <a href={`https://wa.me/91${note.whatsapp?.replace(/\D/g, "")}?text=${waMsg}`}
        target="_blank" rel="noreferrer"
        className="mt-3 flex items-center justify-center gap-2 w-full bg-green-500 text-white py-2 rounded-xl text-sm font-semibold hover:bg-green-600">
        <MessageCircle size={15} /> Contact on WhatsApp
      </a>
    </div>
  );
}

export default function Notes() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeSubject, setActiveSubject] = useState("All");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "", subject: "", semester: "", type: "", description: "", price: "", whatsapp: userProfile?.phone || ""
  });

  useEffect(() => { fetchNotes(); }, []);

  async function fetchNotes() {
    setLoading(true);
    try {
      const snap = await get(dbRef(db, "notes"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setNotes(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setNotes([]);
      }
    } catch (e) {  }
    setLoading(false);
  }

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  function handleImage(e) {
    const f = e.target.files[0];
    if (f) { setImageFile(f); setPreview(URL.createObjectURL(f)); }
  }

  async function handlePost(e) {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || form.title.trim().length < 5) return setError("Title must be at least 5 characters.");
    if (!imageFile) return setError("Please add a photo of your notes. Buyers want to see what they're buying.");
    if (!form.subject) return setError("Please select a subject.");
    if (!form.type) return setError("Please select note type.");
    if (!form.semester) return setError("Please enter semester.");
    if (!form.whatsapp.trim() || !/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, "")))
      return setError("Enter a valid 10-digit WhatsApp number.");
    setPosting(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }
      await push(dbRef(db, "notes"), {
        title: form.title.trim(),
        subject: form.subject,
        semester: Number(form.semester),
        type: form.type,
        description: form.description.trim(),
        price: Number(form.price) || 0,
        whatsapp: form.whatsapp.trim(),
        imageUrl,
        sellerId: currentUser.uid,
        sellerName: userProfile?.name || "Student",
        college: userProfile?.college || "",
        sellerVerified: userProfile?.verified || false,
        createdAt: new Date().toISOString(),
      });
      setForm({ title: "", subject: "", semester: "", type: "", description: "", price: "", whatsapp: userProfile?.phone || "" });
      setImageFile(null); setPreview(null);
      setShowForm(false);
      fetchNotes();
    } catch (err) { setError("Failed to post. Try again.");  }
    setPosting(false);
  }

  const filtered = activeSubject === "All" ? notes : notes.filter(n => n.subject === activeSubject);

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold">Notes Marketplace</h1>
              <p className="text-yellow-100 text-xs mt-0.5">Buy & sell study material on campus</p>
            </div>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-white text-orange-600 px-3 py-1.5 rounded-xl font-semibold text-sm flex items-center gap-1">
            <Plus size={16} /> Sell Notes
          </button>
        </div>
      </div>

      {/* Subject Filter */}
      <div className="px-4 -mt-3 mb-4">
        <div className="bg-white rounded-xl shadow-sm p-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {["All", ...SUBJECTS].map(s => (
              <button key={s} onClick={() => setActiveSubject(s)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition ${activeSubject === s ? "bg-yellow-500 text-white" : "bg-gray-100 text-gray-600"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 space-y-3">
        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-32 bg-white rounded-xl animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <FileText size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No notes found</p>
            <p className="text-gray-400 text-sm mt-1">Be the first to sell your notes!</p>
            <button onClick={() => setShowForm(true)}
              className="mt-4 bg-yellow-500 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
              Sell Your Notes
            </button>
          </div>
        ) : filtered.map(n => <NoteCard key={n.id} note={n} />)}
      </div>

      {/* Post Notes Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">Sell Your Notes</h3>
              <button onClick={() => { setShowForm(false); setError(""); }}><X size={24} /></button>
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-3 pb-24">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                  <AlertCircle size={16} />{error}
                </div>
              )}
              <div onClick={() => document.getElementById("note-img").click()}
                className="border-2 border-dashed border-yellow-300 rounded-xl h-28 flex items-center justify-center cursor-pointer overflow-hidden">
                {preview ? <img src={preview} alt="p" className="h-full w-full object-cover" />
                  : <div className="text-yellow-400 text-center"><Camera size={24} className="mx-auto mb-1" /><p className="text-xs">Photo of notes (required)</p></div>}
              </div>
              <input id="note-img" type="file" accept="image/*" onChange={handleImage} className="hidden" />

              <input name="title" value={form.title} onChange={handleChange} required
                placeholder="Title (e.g. Complete DBMS Notes - Unit 1-5)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-yellow-400 focus:border-transparent" />

              <div className="grid grid-cols-2 gap-3">
                <select name="subject" value={form.subject} onChange={handleChange} required
                  className="border border-gray-200 rounded-xl px-3 py-3 text-sm focus:ring-2 focus:ring-yellow-400 text-gray-700">
                  <option value="">Subject *</option>
                  {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select name="semester" value={form.semester} onChange={handleChange} required
                  className="border border-gray-200 rounded-xl px-3 py-3 text-sm focus:ring-2 focus:ring-yellow-400 text-gray-700">
                  <option value="">Semester *</option>
                  {[1,2,3,4,5,6,7,8].map(s => <option key={s} value={s}>Sem {s}</option>)}
                </select>
              </div>

              <select name="type" value={form.type} onChange={handleChange} required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-yellow-400 text-gray-700">
                <option value="">Note Type *</option>
                {NOTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>

              <textarea name="description" value={form.description} onChange={handleChange} rows={2}
                placeholder="What's covered, any special details..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-yellow-400 resize-none" />

              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                <input name="price" type="number" min="0" value={form.price} onChange={handleChange} required
                  placeholder="Price (0 = Free)"
                  className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-sm focus:ring-2 focus:ring-yellow-400" />
              </div>

              <input name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} required
                placeholder="WhatsApp number *"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-yellow-400" />

              <button type="submit" disabled={posting}
                className="w-full bg-yellow-500 text-white py-3.5 rounded-xl font-semibold hover:bg-yellow-600 disabled:opacity-50 text-base">
                {posting ? "Posting..." : "Post Notes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
